import { IntlProvider } from 'react-intl';
import { Outlet, useLocation } from 'react-router-dom';
import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';

import { DEFAULT_LOCALE } from '../configuration/lang/lang';
import { isUserSessionExpired } from '../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../configuration/setup/hooks';
import { getDisplayMessages, getLocale } from '../configuration/lang/langSlice';
import DefaultRedirect from '../routes/DefaultRedirect';
import RouteUpdater from '../routes/RouteUpdater';
import { getSessionExpiredAcknowledged, hideSessionExpiredDialog } from '../data/appSlice';
import AppHeader from '../features/header/AppHeader';

import './App.css';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { AppContext } from './AppContext';
import { flatten } from 'flat';
import { WebSocketProvider } from '../utils/websocket/websocketContext';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { useFetch } from '../utils/hooks/useFetch';
import { storeAllChargers, storeAllGrids } from '../pages/infrastructure/infrastructureSlice';
import { storeMissions } from '../pages/missions/missionsSlice';
import { storeVehicles } from '../pages/fleet/fleetSlice';

const AppLayout = () => {
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const sidebarRef = useRef<HTMLDivElement>(null);
    const [sidebarContent, setSidebarContent] = useState<ReactNode>(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | null>(null);

    const userLocale = useAppSelector(getLocale);
    const displayMessages = useAppSelector(getDisplayMessages);
    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const sessionExpiredAcknowledged = useAppSelector(getSessionExpiredAcknowledged);

    useFetch({
        url: 'station-service/stations',
        reduxAction: storeAllChargers,
        autoFetch: true,
    });
    useFetch({
        url: 'mission-service/missions',
        reduxAction: storeMissions,
        autoFetch: true,
    });
    useFetch({
        url: 'mission-service/vehicle',
        reduxAction: storeVehicles,
        autoFetch: true,
    });
    useFetch({
        url: 'station-service/grid',
        reduxAction: storeAllGrids,
        autoFetch: true,
    });

    if (!(displayMessages && userLocale)) {
        return null;
    }

    const showSidebar = (content: ReactNode, onClose?: () => void) => {
        setSidebarContent(content);
        setIsSidebarOpen(true);
        setOnCloseCallback(() => onClose || null); // callback to handle sidebar header button
    };

    const hideSidebar = () => {
        setIsSidebarOpen(false);
        setSidebarContent(null);
        if (onCloseCallback) {
            onCloseCallback();
        }
        setOnCloseCallback(null);
    };

    const handleSessionExpiredDialogClose = () => dispatch(hideSessionExpiredDialog());
    const showSessionExpired = isSessionExpired && !sessionExpiredAcknowledged;

    useEffect(() => {
        hideSidebar();
    }, [pathname]);

    return (
        <IntlProvider
            defaultLocale={DEFAULT_LOCALE}
            key={userLocale}
            locale={userLocale}
            messages={flatten(displayMessages)}
        >
            <WebSocketProvider>
                <AppContext.Provider value={{ sidebarRef, showSidebar, hideSidebar }}>
                    <ApplicationLayout data-testid="app-layout">
                        <ApplicationLayout.Header>
                            <AppHeader />
                        </ApplicationLayout.Header>

                        <ApplicationLayout.Sidebar ref={sidebarRef} className="right">
                            <Sidebar fly closed={!isSidebarOpen} onClose={hideSidebar} width={500}>
                                {sidebarContent}
                            </Sidebar>
                        </ApplicationLayout.Sidebar>
                        <ApplicationLayout.Body>
                            <NotificationsContainer />
                            <SessionExpiredDialog
                                locale={userLocale}
                                onClose={handleSessionExpiredDialogClose}
                                show={showSessionExpired}
                            />
                            <div className="container">
                                <Outlet />
                                <RouteUpdater />
                                <DefaultRedirect />
                            </div>
                        </ApplicationLayout.Body>
                    </ApplicationLayout>
                </AppContext.Provider>
            </WebSocketProvider>
        </IntlProvider>
    );
};

export default AppLayout;
